package club.srams.website.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initFieldTripIntroductionPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("fieldTripIntroduction.md", FrontMatterElement.Builder { addScalar("title", "Field Trip Introduction"); }))
}

@Page("/field-trip-introduction")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun FieldTripIntroductionPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("rock-hounding-in-washington-state") }) {
        org.jetbrains.compose.web.dom.Text("Rock hounding in Washington state")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Washington is a premier destination for rockhounding due to its diverse volcanic and glacial history. Collectors can find everything from agate and jasper on coastal beaches to quartz crystals in the mountains and petrified wood (the state gem) in the central plains.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H3(attrs = { id("coastal-beaches-agate-jasper") }) {
        org.jetbrains.compose.web.dom.Text("Coastal Beaches (Agate & Jasper):")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Shi Shi Beach & Rialto Beach: Famous for ocean-tumbled agates and orbicular jasper.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("North Beach County Park:")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H3(attrs = { id("central-eastern-wa-petrified-wood-agate") }) {
        org.jetbrains.compose.web.dom.Text("Central & Eastern WA (Petrified Wood & Agate):")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Saddle Mountains (Mattawa): A legendary spot for collecting opalized and petrified wood.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Red Top Mountain (Cle Elum): Popular for blue agate nodules, geodes, and jasper.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H3(attrs = { id("mountainous-regions-crystals") }) {
        org.jetbrains.compose.web.dom.Text("Mountainous Regions (Crystals):")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Hansen Creek (Snoqualmie Pass): One of the best public sites for digging clear quartz and amethyst scepters.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Walker Valley (Skagit County): A well-known public site for geodes containing quartz or amethyst crystals.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H3(attrs = { id("northern-sound-jade") }) {
        org.jetbrains.compose.web.dom.Text("Northern Sound (Jade):")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Whidbey Island: Known for finding nephrite jade along the shoreline, especially after high tidal action.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("essential-rules-regulations") }) {
        org.jetbrains.compose.web.dom.Text("Essential Rules & Regulations")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Before you head out, you must understand the legalities of where and what you can collect:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Allowed Areas: Casual, non-commercial collecting is generally permitted on Federal Lands (US Forest Service and BLM) and State-owned aquatic lands.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Prohibited Areas: Rockhounding is strictly forbidden in National Parks, National Monuments, State Parks (like Ginkgo Petrified Forest State Park), and Tribal Lands.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Personal Limits: On BLM lands, you can typically collect up to 25 pounds per day (plus one larger piece) and 250 pounds per year for personal use.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Restricted Items: You cannot collect vertebrate fossils (bones, teeth), meteorites, or archaeological artifacts without a special permit.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Private Property: Always obtain explicit permission from the landowner before entering or collecting on private land.")
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("additional-information") }) {
        org.jetbrains.compose.web.dom.Text("Additional Information:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            com.varabyte.kobweb.silk.components.navigation.Link("https://dnr.wa.gov/washington-geological-survey/explore-popular-geology/rockhounding") {
                org.jetbrains.compose.web.dom.Text("Washington State DNR")
            }
        }
    }
}
